import { Button, cn, Typography, useTranslate } from '@prenuvo/halo';

import { Required } from '@/shared/ui';

import { REASON_FOR_ACCESS } from './patient-contact-dialog.constant';
import { useCreateActivityLog } from './use-create-activity-log.hook';

export type PatientContactConfirmationSectionProps = {
  onCancel: () => void;
  onNext: () => void;
  reason: null | string;
  setReason: (reason: string) => void;
};

export function PatientContactConfirmationSection({
  onCancel,
  onNext,
  reason,
  setReason,
}: PatientContactConfirmationSectionProps) {
  const { createActivityLog } = useCreateActivityLog();
  const translate = useTranslate();

  const onNextButtonHandler = () => {
    createActivityLog(
      {
        action: 'CREATE' as const,
        fullObject: { reasonForAccessingPtInfo: reason ? translate(reason) : undefined },
        objectDiff: null,
      },
      () => onNext(),
    );
  };

  return (
    <div>
      <Typography
        as="p"
        className="text-neutral-50"
        translationKey="FCT.REPORTING.HEADER.CONTACT_DETAILS.INFO_DESCRIPTION"
      />
      <ul className="list-disc pt-3 pl-6">
        <li className="pt-1">
          <Typography
            className="text-neutral-50"
            translationKey="FCT.REPORTING.HEADER.CONTACT_DETAILS.LABEL.LEGAL_NAME"
          />
        </li>
        <li className="pt-1">
          <Typography
            className="text-neutral-50"
            translationKey="FCT.REPORTING.HEADER.CONTACT_DETAILS.LABEL.PHONE_NUMBER"
          />
        </li>
      </ul>
      <div className="flex flex-col items-center pt-4">
        <div className="flex">
          <Required isRequired>
            <Typography
              className="text-neutral-400 dark:text-neutral-400"
              translationKey="FCT.REPORTING.HEADER.CONTACT_DETAILS.REASON_TITLE"
            />
          </Required>
        </div>
        <div className="flex pt-3">
          {REASON_FOR_ACCESS.map((label) => (
            <Button
              key={label}
              className={{
                root: cn(
                  'mr-3 rounded-xl border border-neutral-800 hover:border-blue-500 hover:bg-blue-500/10 dark:border-neutral-800 dark:hover:border-blue-500 dark:hover:bg-blue-500/10',
                  {
                    'border-blue-500 bg-blue-500/10 dark:border-blue-500 dark:bg-blue-500/10':
                      reason === label,
                  },
                ),
                text: 'text-base font-normal',
              }}
              data-testid={`${label}-btn`}
              onClick={() => setReason(label)}
              size="md"
              translationKey={label}
              variant="outlined"
            />
          ))}
        </div>
      </div>
      <div className="mt-6 flex justify-end gap-4">
        <Button
          className={{ root: 'px-8' }}
          data-testid="cancel-button"
          onClick={onCancel}
          translationKey="FCT.REPORTING.HEADER.CONTACT_DETAILS.LABEL.CANCEL"
          variant="outlined"
        />
        <Button
          className={{ root: 'px-8' }}
          data-testid="next-button"
          disabled={!reason}
          onClick={onNextButtonHandler}
          translationKey="FCT.REPORTING.HEADER.CONTACT_DETAILS.LABEL.NEXT"
          variant="primary"
        />
      </div>
    </div>
  );
}
